import { ChevronRight } from '@/src/assets/icons/ChevronRight'
import { mesaColorNames, typography } from '@/src/common/theme'
import { BoxedIcon } from '@/src/design-system/BoxedIcon'
import { UserPushNotificationStatus } from '@api/mesa/graphql'
import { StyleSheet, Text, View, type ViewStyle } from 'react-native'
import { ListItem } from 'tamagui'
import {
  notificationCenterHorizontalPadding,
  notificationCenterVerticalPadding,
} from '@/src/experiences/notifications/screens/Center'
import { HomeOutlined } from '@/src/assets/icons/HomeOutlined'
import { formatToTextMonth } from '@/src/common/utils/date'
import { useMutation } from '@apollo/client'
import { Mutations } from '@/src/experiences/notifications/gql'
import { StatementIcon } from '@/src/assets/icons/StatementIcon'
import { PaymentIcon } from '@/src/assets/icons/PaymentIcon'
import { PurchaseIcon } from '@/src/assets/icons/PurchaseIcon'
import Feather from '@expo/vector-icons/Feather'
import ReturnIcon from '@/src/assets/icons/ReturnIcon'
import { Link, usePathname } from 'expo-router'
import * as Linking from 'expo-linking'
import type { DatedUserNotification } from '@/src/experiences/notifications/hooks/useUserNotifications'
import { GET_UNREAD_NOTIFICATIONS } from '@/src/common/components/Profile/NotificationsEntryPoint'
import { safeLineHeight } from '@/src/common/utils/safeLineHeight'
import { GET_USER_NOTIFICATIONS } from '@/src/experiences/notifications/gql/queries'
import { NotificationsRoutes } from '@/src/experiences/notifications/navigation/routes'

const bodyLineHeight = typography['label/xs-regular'].lineHeight * 1.5
const bodyLineHeightOffset = typography['label/xs-regular'].lineHeight - bodyLineHeight

const gap = 8 satisfies ViewStyle['gap']

export function FeedItem({
  date,
  notification: { id, title, body, pushNotificationStatus, deepLinkPath },
}: DatedUserNotification) {
  const showIndicator = pushNotificationStatus !== UserPushNotificationStatus.Opened

  const pathname = usePathname()
  const deepLinkable =
    deepLinkPath &&
    deepLinkPath !== NotificationsRoutes.Center.route &&
    Linking.parse(Linking.createURL(pathname)).path !==
      Linking.parse(Linking.createURL(deepLinkPath)).path

  const pressable = deepLinkable || showIndicator

  const icon = (() => {
    switch (title) {
      case 'New Statement Available':
        return (
          <StatementIcon
            color={mesaColorNames['Mesa - Brand/Secondary #223843']}
            height={20}
            width={20}
          />
        )
      case 'Payment Received':
        return (
          <PaymentIcon
            color={mesaColorNames['Mesa - Brand/Secondary #223843']}
            height={20}
            width={20}
          />
        )
      case 'Approved Transaction':
      case 'Pending Transaction':
        return body.startsWith('Return') ? (
          <ReturnIcon
            color={mesaColorNames['Mesa - Brand/Secondary #223843']}
            height={20}
            width={20}
          />
        ) : (
          <PurchaseIcon
            color={mesaColorNames['Mesa - Brand/Secondary #223843']}
            height={20}
            width={20}
          />
        )
      case 'Declined Transaction':
        return (
          <Feather
            color={mesaColorNames['Mesa - Brand/Secondary #223843']}
            size={18}
            name="alert-triangle"
          />
        )
      default:
        return (
          <HomeOutlined
            outlineColor={mesaColorNames['Mesa - Brand/Secondary #223843']}
            height={20}
            width={20}
          />
        )
    }
  })()

  const [updateNotificationPushStatus] = useMutation(
    Mutations.UPDATE_USER_NOTIFICATION_PUSH_STATUS,
    { refetchQueries: [GET_UNREAD_NOTIFICATIONS, GET_USER_NOTIFICATIONS] },
  )

  const listItem = (
    <ListItem
      {...(deepLinkable && { tag: 'a' })}
      {...(showIndicator && {
        onPress() {
          void updateNotificationPushStatus({
            variables: { id, pushNotificationStatus: UserPushNotificationStatus.Opened },
          })
        },
      })}
      {...(pressable && { pressStyle: { opacity: 0.2, backgroundColor: 'transparent' } })}
      style={StyleSheet.flatten([
        {
          minHeight: 72,
          textDecorationLine: 'none',
          paddingLeft: notificationCenterHorizontalPadding,
          paddingRight: notificationCenterHorizontalPadding,
          paddingTop: notificationCenterVerticalPadding,
          paddingBottom: notificationCenterVerticalPadding,
          gap,
          justifyContent: 'flex-start',
        },
        pressable && { cursor: 'pointer' },
      ])}
      animation="75ms"
      // @ts-expect-error tamagui typescript issue?
      hoverStyle={{ backgroundColor: `${mesaColorNames['semantic/fg/subtle #898D8F']}20` }}
      backgroundColor="transparent"
    >
      <BoxedIcon icon={icon}>
        {showIndicator && (
          <View
            style={{
              position: 'absolute',
              top: -4,
              left: -4,
              zIndex: 1,
              alignItems: 'center',
              flexDirection: 'row',
              justifyContent: 'center',
              backgroundColor: mesaColorNames['Mesa Third #E78A43'],
              borderRadius: 12,
              height: 12,
              width: 12,
            }}
          />
        )}
      </BoxedIcon>

      <View
        style={{
          gap,
          flexShrink: 1,
          flexGrow: 1,
          alignSelf: 'stretch',
          flexDirection: 'column',
          justifyContent: 'space-evenly',
        }}
      >
        <View
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap,
          }}
        >
          <Text
            style={[
              { color: mesaColorNames['global/grey/80 #2F3133'], flexShrink: 1 },
              typography['label/sm-regular'],
              safeLineHeight(typography['label/sm-regular'].lineHeight),
            ]}
            numberOfLines={1}
            ellipsizeMode="tail"
          >
            {title}
          </Text>
          <Text
            style={[
              typography['label/xs-regular'],
              { color: mesaColorNames['semantic/fg/subtle #898D8F'], flexShrink: 0 },
            ]}
          >
            {formatToTextMonth(date)}
          </Text>
        </View>

        {body && (
          <Text
            style={[
              typography['label/xs-regular'],
              {
                lineHeight: bodyLineHeight,
                marginTop: bodyLineHeightOffset,
                color: mesaColorNames['Greyscale/500 #6E7375'],
              },
            ]}
            numberOfLines={2}
            ellipsizeMode="tail"
          >
            {body}
          </Text>
        )}
      </View>

      <View
        style={{
          marginLeft: 'auto',
          justifyContent: 'center',
          alignItems: 'center',
          flexShrink: 0,
          flexGrow: 0,
          width: 18,
        }}
      >
        {deepLinkable && <ChevronRight color={mesaColorNames['Greyscale/500 #6E7375']} />}
      </View>
    </ListItem>
  )

  return deepLinkable ? (
    <Link
      asChild
      dismissTo
      // @ts-expect-error // FORGIVE ME BRAIN FOR I HAVE SINNED
      href={deepLinkPath}
    >
      {listItem}
    </Link>
  ) : (
    listItem
  )
}
